import React, {useEffect, useMemo, useState} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from 'components/atoms/Modal'
import styled from 'styled-components'
import { Tabs, Tab, Button, TextField, List, ListItem, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material'
import {
  getUsersAssociatedToBp,
  getSelectedBp,
  selectBulkShareStatus,
  selectUserFoundByEmail,
  localSearchFileResults
} from 'features/bpDetails/ducks/selectors'
import {bulkShareFilesRoutine, checkUserByEmailRoutine, fetchSharedUsersForFilesRoutine} from '../ducks/actions'
import { styled as muiStyled } from '@mui/material/styles'
import {groupBy, uniq} from "ramda"
import {USER_ROLES} from "../../../utils/userRoles"
import {buildingRoleLabel} from "../../../utils/roles"
import { selectFilesSharedUsers } from 'features/bpDetails/ducks/selectors'
import { fetchBpUnitsRoutine } from 'ducks/units/actions'
import { fetchBp } from 'services/BpService'
import * as unitsService from 'services/unitsService'
import { SkeletonRows } from 'components/Skeleton'

const shareBulkBpPoolCache = new Map()
const shareBulkUnitsPoolCache = new Map()

const BulkShareFilesModal = ({fileIds = [], open, onClose, selectedFiles, initialTab = 0, bp: bpProp = null }) => {
  const safeSelectedFiles = selectedFiles || fileIds || []
  const [tabIndex, setTabIndex] = useState(initialTab)
  // When the host re-opens the modal with a different `initialTab`, sync —
  // useState's initial value is only consulted on first render.
  useEffect(() => {
    if (open) setTabIndex(initialTab)
  }, [open, initialTab])
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [email, setEmail] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const storeConnectedUsers = useSelector(getUsersAssociatedToBp)
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const needsBulkPoolFetch = Boolean(bpProp?.id) && bpProp.id !== selectedBp?.id
  const [fetchedPoolBp, setFetchedPoolBp] = useState(null)
  const [fetchedUnits, setFetchedUnits] = useState(null)
  const [isLoadingConnectedUsers, setIsLoadingConnectedUsers] = useState(false)
  const [isLoadingBulkUnits, setIsLoadingBulkUnits] = useState(false)
  const dispatch = useDispatch()
  const { loading } = useSelector(selectBulkShareStatus)
  const userFound = useSelector(selectUserFoundByEmail)
  const [confirmDialogContent, setConfirmDialogContent] = useState({ title: '', body: '' })
  const { data: sharedUsers } = useSelector(selectFilesSharedUsers)
  const [localSelectedFiles, setLocalSelectedFiles] = useState([])
  const localSearchResults = useSelector(localSearchFileResults)
  const [selectedUnitIds, setSelectedUnitIds] = useState([])
  const storeUnits = useSelector(state => state.bpDetails.units || [])
  const units = needsBulkPoolFetch ? (fetchedUnits || []) : storeUnits
  const connectedUsers = useMemo(() => {
    if (!needsBulkPoolFetch) return storeConnectedUsers
    if (!fetchedPoolBp) return []
    const tag = (users, type) => (users || []).map(user => ({ ...user, relationType: type }))
    const allUsers = [
      ...tag(fetchedPoolBp.agents, buildingRoleLabel('agent')),
      ...tag(fetchedPoolBp.owners, buildingRoleLabel('owner')),
      ...tag(fetchedPoolBp.responsiblePersons, 'Responsible'),
      ...tag(fetchedPoolBp.occupants, 'Occupant'),
      ...tag(fetchedPoolBp.principalAccountablePersons, 'PrincipalAccountable'),
      ...tag(fetchedPoolBp.principalDesigners, 'PrincipalDesigner'),
      ...tag(fetchedPoolBp.prospectivePurchasers, 'Buyer')
    ]
    const seen = new Set()
    return allUsers.filter(user => {
      if (seen.has(user.id)) return false
      seen.add(user.id)
      return true
    })
  }, [needsBulkPoolFetch, fetchedPoolBp, storeConnectedUsers])
  const selectedFileNames = useMemo(() => {
    return localSearchResults
      .filter(file => safeSelectedFiles.includes(file.id))
      .map(file => file.displayName || file.name || "Unnamed file")
  }, [localSearchResults, safeSelectedFiles])

  const fullySharedUserIds = useMemo(() => {
    return sharedUsers.internalUsers
      .filter(user =>
        user.fileIds &&
        safeSelectedFiles.every(fileId => user.fileIds.includes(fileId))
      )
      .map(user => user.id)
  }, [sharedUsers, safeSelectedFiles])

  useEffect(() => {
    if (open) {
      setSelectedUserIds(fullySharedUserIds)
    }
  }, [open, fullySharedUserIds])

  useEffect(() => {
    if (!open || !needsBulkPoolFetch) return

    const cached = shareBulkBpPoolCache.get(bpProp.id)
    if (cached) {
      setFetchedPoolBp(cached)
      return
    }

    let cancelled = false
    setIsLoadingConnectedUsers(true)
    fetchBp(bpProp.id)
      .then(response => {
        const fullBp = response?.data?.data
        if (fullBp) {
          shareBulkBpPoolCache.set(bpProp.id, fullBp)
          if (!cancelled) setFetchedPoolBp(fullBp)
        }
      })
      .catch(error => {
        console.error('Failed to fetch building user pool:', error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingConnectedUsers(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, needsBulkPoolFetch, bpProp?.id])

  useEffect(() => {
    if (!open || !needsBulkPoolFetch) return

    const cached = shareBulkUnitsPoolCache.get(bpProp.id)
    if (cached) {
      setFetchedUnits(cached)
      return
    }

    let cancelled = false
    setIsLoadingBulkUnits(true)
    unitsService.fetchBpUnits({ bpId: bpProp.id })
      .then(response => {
        const list = response?.data?.data || []
        shareBulkUnitsPoolCache.set(bpProp.id, list)
        if (!cancelled) setFetchedUnits(list)
      })
      .catch(error => {
        console.error('Failed to fetch building units:', error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBulkUnits(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, needsBulkPoolFetch, bpProp?.id])

  const handleTabChange = (_, newIndex) => setTabIndex(newIndex)

  const groupedConnectedUsers = useMemo(() => {
    const grouped = groupBy(u => u.id, connectedUsers || [])
    return Object.values(grouped).map(users => {
      const base = users[0]
      return {
        ...base,
        roles: uniq(users.map(u => u.relationType))
      }
    })
  }, [connectedUsers])

  const handleToggleUser = userId => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleShareClick = () => {
    if (tabIndex === 1 && email.length >= 3) {
      dispatch(checkUserByEmailRoutine.trigger({
        email,
        bpId: bp.id,
      }))
    } else {
      const filesListString = selectedFileNames.length > 0
        ? selectedFileNames.join(", ")
        : `${safeSelectedFiles.length} file${safeSelectedFiles.length !== 1 ? 's' : ''}`

      const usersToShare = selectedUserIds.filter(id => !fullySharedUserIds.includes(id))
      const usersToShareCount = usersToShare.length

      setConfirmDialogContent({
        title: `You are about to share ${filesListString} with ${usersToShareCount} user${usersToShareCount !== 1 ? 's' : ''}.`,
      })
      setConfirmOpen(true)
    }
  }
  useEffect(() => {
    if (tabIndex !== 1 || !email || !userFound) return

    const lowerEmail = email.toLowerCase()
    const existingUsers = userFound.existingUsers || []
    const usersWithAccess = userFound.usersWithAccess || []

    const userExists = existingUsers.some(user => user.email.toLowerCase() === lowerEmail)
    const hasAccess = usersWithAccess.includes(lowerEmail)

    if (!userExists) {
      setConfirmDialogContent({
        title: 'Invite to view-only',
        body: `${email} doesn’t have a Building Passport account. A view-only email invitation to the selected files will be sent.`,
        note: 'Other files remain private unless you invite them to the full Building Passport using the Access tab.\n'
      })
    } else {
      const title = 'This email is already associated with a Building Passport account'
      const body = hasAccess
        ? `${email} already has access to this Building Passport and will get access to these files only as a User. Would you like to continue?`
        : `${email} will get access to these files only on this Building Passport as a User. Would you like to continue?`

      setConfirmDialogContent({
        title,
        body,
        note: 'They will not be able to edit or delete these files.'
      })
    }

    setConfirmOpen(true)
  }, [userFound])

  const closeConfirmDialog = () => {
    setConfirmOpen(false)
  }

  const handleConfirmShare = () => {
    let payload = {}

    if (tabIndex === 0) {
      const usersToShare = selectedUserIds.filter(id => !fullySharedUserIds.includes(id))
      payload = {
        fileIds: safeSelectedFiles,
        bpId: bp.id,
        users: usersToShare.map(id => ({ user_id: id }))
      }
    } else if (tabIndex === 1) {
      payload = {
        fileIds: safeSelectedFiles,
        bpId: bp.id,
        users: [{ email }]
      }
    } else if (tabIndex === 2) {
      payload = {
        fileIds: safeSelectedFiles,
        bpId: bp.id,
        units: selectedUnitIds.map(id => ({ unit_id: id }))
      }
    }

    dispatch(bulkShareFilesRoutine.trigger(payload))
    closeConfirmDialog()
    onClose()
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title="Share selected files">
        <Wrapper>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            <Tab label="Existing users" />
            <Tab label="Invite by email" />
            <Tab label="Share with Units" />
          </Tabs>

          <SegmentedTabs>
            <SegmentButton
              type="button"
              $active={tabIndex === 0}
              onClick={() => setTabIndex(0)}
            >
              Existing users
            </SegmentButton>
            <SegmentButton
              type="button"
              $active={tabIndex === 1}
              onClick={() => setTabIndex(1)}
            >
              Invite by email
            </SegmentButton>
            <SegmentButton
              type="button"
              $active={tabIndex === 2}
              onClick={() => setTabIndex(2)}
            >
              Share with Units
            </SegmentButton>
          </SegmentedTabs>

          {tabIndex === 0 && (
            <UsersTab>
              {isLoadingConnectedUsers ? (
                <SkeletonRows rows={4} gap={16} widths={['60%', '30%']} />
              ) : (
                <>
                  {connectedUsers.length > 0 && (
                    <ActionsRow>
                      <ActionButton onClick={() =>
                        setSelectedUserIds(connectedUsers.map(user => user.id))
                      }>
                        Select all
                      </ActionButton>
                      <ActionButton onClick={() => setSelectedUserIds([])}>
                        Deselect all
                      </ActionButton>
                    </ActionsRow>
                  )}

                  <StyledList dense>
                    {groupedConnectedUsers.map(user => {
                      const isChecked = selectedUserIds.includes(user.id)
                      const isFullyShared = fullySharedUserIds.includes(user.id)
                      return (
                        <div key={user.id}>
                          <StyledListItem
                            onClick={() => !isFullyShared && handleToggleUser(user.id)}
                          >
                            <Checkbox
                              edge="start"
                              checked={isChecked}
                              disabled={isFullyShared}
                              tabIndex={-1}
                              disableRipple
                            />
                            <UserInfo>
                              <div className="name">{user.firstName} {user.lastName}</div>
                              <div className="email">{user.email}</div>
                              {user.roles && user.roles.length > 0 && (
                                <div className="tag">
                                  User role: {user.roles.map(role => USER_ROLES[role] || role).join(', ')}
                                </div>
                              )}
                              {isFullyShared && (
                                <div className="tag" style={{ color: '#C24A2E', fontWeight: 500 }}>
                                  Already shared
                                </div>
                              )}
                            </UserInfo>
                          </StyledListItem>
                        </div>
                      )
                    })}
                  </StyledList>
                </>
              )}
            </UsersTab>
          )}

          {tabIndex === 1 && (
            <EmailTab>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </EmailTab>
          )}

          {tabIndex === 2 && (
            <UnitsTab>
              {isLoadingBulkUnits ? (
                <SkeletonRows rows={4} gap={16} widths={['60%', '30%']} />
              ) : units.length === 0 ? (
                <Typography>No units available for this Building Passport.</Typography>
              ) : (
                <>
                  <ActionsRow>
                    <ActionButton onClick={() => setSelectedUnitIds(units.map(u => u.id))}>
                      Select all
                    </ActionButton>
                    <ActionButton onClick={() => setSelectedUnitIds([])}>
                      Deselect all
                    </ActionButton>
                  </ActionsRow>

                  <StyledList dense>
                    {units.map(unit => {
                      const isChecked = selectedUnitIds.includes(unit.id)
                      return (
                        <StyledListItem key={unit.id} onClick={() => {
                          setSelectedUnitIds(prev =>
                            prev.includes(unit.id)
                              ? prev.filter(id => id !== unit.id)
                              : [...prev, unit.id]
                          )
                        }}>
                          <Checkbox
                            edge="start"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                          />
                          <UserInfo>
                            <div className="name">{unit.name}</div>
                            {unit.address && (
                              <div className="email">{unit.address}</div>
                            )}
                          </UserInfo>
                        </StyledListItem>
                      )
                    })}
                  </StyledList>
                </>
              )}
            </UnitsTab>
          )}

          <Footer>
            <ShareButton
              variant="contained"
              color="primary"
              onClick={handleShareClick}
              disabled={
                loading ||
                (tabIndex === 0 && selectedUserIds.length === 0) ||
                (tabIndex === 1 && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) ||
                (tabIndex === 2 && selectedUnitIds.length === 0)
              }
            >
              {loading ? 'Sharing...' : 'Share'}
            </ShareButton>
          </Footer>
        </Wrapper>
      </Modal>

      <Dialog
        open={confirmOpen}
        onClose={closeConfirmDialog}
        sx={{ zIndex: 99999 }}
      >
        <DialogTitle>{confirmDialogContent.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialogContent.body}</Typography>
          {confirmDialogContent.note && (
            <Typography variant="body2" style={{ marginTop: 12, color: '#666' }}>
              {confirmDialogContent.note}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <ShareButton onClick={closeConfirmDialog}>Cancel</ShareButton>
          <ShareButton onClick={handleConfirmShare} variant="contained" color="primary" autoFocus>
            Confirm
          </ShareButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BulkShareFilesModal

// MODIFICARE 3: Wrapper-ul devine invizibil când celălalt modal este deschis
const Wrapper = styled.div`
  padding: 20px;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
  opacity: ${({ isHidden }) => (isHidden ? 0 : 1)};
  height: ${({ isHidden }) => (isHidden ? '0px' : 'auto')};
  overflow: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};

  @media (max-width: 768px) {
    padding: 12px 0 0;
  }
`

const SegmentedTabs = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 0 12px 12px;
  }
`

const SegmentButton = styled.button`
  border: 1px solid ${({ $active }) => ($active ? '#C24A2E' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? '#fff5f2' : '#fff')};
  color: ${({ $active }) => ($active ? '#C24A2E' : '#6b7280')};
  font-size: 12px;
  font-weight: 700;
  padding: 8px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const UsersTab = styled.div`
  margin-top: 20px;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-top: 8px;
    max-height: 52vh;
    padding: 0 12px;
  }
`

const EmailTab = styled.div`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 8px;
    padding: 0 12px;
  }
`

const Footer = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 12px;
    margin-top: 12px;
    justify-content: stretch;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: stretch;
    gap: 8px;
    padding: 0 12px;
  }
`

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary || '#C24A2E'};
  cursor: pointer;
  font-weight: 500;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    flex: 1;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px 10px;
    text-align: center;
    text-decoration: none;
  }
`

const StyledList = styled(List)`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;

  @media (max-width: 768px) {
    max-height: 52vh;
    padding-right: 0;
  }
`

const StyledListItem = styled(ListItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
  }
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    font-weight: 500;
  }

  .email {
    font-size: 13px;
    color: #666;
  }

  .tag {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }
`
const UnitsTab = styled.div`
  margin-top: 20px;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-top: 8px;
    max-height: 52vh;
    padding: 0 12px;
  }
`

const ShareButton = muiStyled(Button)({
  backgroundColor: '#C24A2E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#a63f26'
  }
})
